import { z } from "zod";

import i18n from "@/i18n";
import { InputField } from "@/features/FormCard/Fields";
import type { EditForm } from "@/features/Edit/types.ts";

const table_name = "k1_management_companies";

const label = (key: string, defaultValue: string) =>
  i18n.t(`${table_name}.columns.${key}`, { ns: "tables", defaultValue });

// Canonicalizes the recognized shapes (comma string, string array) into a
// plain string array. Unrecognized shapes (e.g. a legacy JSON object) pass
// through untouched so the outer schema below rejects them with a visible
// error instead of silently collapsing them to an empty array.
const toContactsShape = (value: unknown): unknown => {
  if (value == null || value === "") return [];

  if (typeof value === "string") {
    return value
      .split(",")
      .map((email) => email.trim())
      .filter(Boolean);
  }

  if (Array.isArray(value)) {
    return value.map((email) => String(email).trim());
  }

  return value;
};

// Applied to both the fetched and submitted data before diffing, so an
// untouched field never appears "changed" just because of shape/whitespace
// differences between the raw fetched value and the canonical form.
const adjustContactsForCompare = (data: Record<string, any>) => ({
  ...data,
  contacts: toContactsShape(data.contacts),
});

const contactsField = () =>
  z.preprocess(
    (value) => toContactsShape(value),
    z.array(z.email("Invalid email address")),
  );

const contactsToDisplayString = (value: unknown): string => {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "string") return value;
  return "";
};

export const k1ManagementCompaniesEditForm: EditForm = {
  table_name,
  formSchema: z.object({
    name: z.string().min(1, "Name is required"),
    country: z.string().nullable().optional(),
    address: z.string().nullable().optional(),
    contacts: contactsField(),
  }),
  adjustData: adjustContactsForCompare,
  formBodyContent: (form, multiFieldOptions) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <InputField
        name="name"
        label={label("name", "Name")}
        isRequired
        className="md:col-span-2"
        control={form.control}
        fieldOptions={{ isReadOnly: true }}
        multiFieldOptions={multiFieldOptions}
      />
      <InputField
        name="country"
        label={label("country", "Country")}
        control={form.control}
        multiFieldOptions={multiFieldOptions}
      />
      <InputField
        name="address"
        label={label("address", "Address")}
        control={form.control}
        multiFieldOptions={multiFieldOptions}
      />
      <InputField
        name="contacts"
        label={label("contacts", "Contacts (comma-separated emails)")}
        className="md:col-span-2"
        control={form.control}
        valueMapper={contactsToDisplayString}
        multiFieldOptions={multiFieldOptions}
      />
    </div>
  ),
};
